import React, { useState, useEffect } from 'react';
import { getEmployee, clockIn, clockOut, submitReport, getLiveDashboard, verifyEmployeePin, getTodayAssignments, getEmployeeAvailability, submitAvailabilityException, deleteAvailabilityException, createAssignment } from '../api';
import api from '../api';

function EmployeePortal() {
  const [step, setStep] = useState('login'); // 'login' or 'action'
  const [employeeIdInput, setEmployeeIdInput] = useState('');
  const [pinInput, setPinInput] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [currentAssignment, setCurrentAssignment] = useState(null);
  const [todayShifts, setTodayShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);
  const [reportData, setReportData] = useState({
    reservations: '',
    total_cars: '',
    free_cars: '',
    paid_cars: '',
    cars_on_lot: '',
    cash_collected: '',
    card_collected: '',
    is_final: false
  });
  const [availabilityData, setAvailabilityData] = useState({
    start_date: '',
    end_date: '',
    reason: 'personal',
    notes: ''
  });
  const [selectedShift, setSelectedShift] = useState(null);
  const [isClockedIn, setIsClockedIn] = useState(false);
  const [clockedInLocation, setClockedInLocation] = useState(null);
  const [clockedInShiftId, setClockedInShiftId] = useState(null);
  const [existingExceptions, setExistingExceptions] = useState([]);
  const [weekSchedule, setWeekSchedule] = useState([]);
  const tenantId = 1;

  // Check for saved login on mount
  useEffect(() => {
  const savedEmployeeId = localStorage.getItem('employeeId');
  if (savedEmployeeId) {
    autoLogin(savedEmployeeId);
  } else {
    setLoading(false);
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);

  // Fetch today's shifts when employee logs in
  useEffect(() => {
    if (selectedEmployee) {
      fetchTodayShifts();
    }
  }, [selectedEmployee]);

  const autoLogin = async (employeeId) => {
    try {
      const response = await getEmployee(employeeId);
      setSelectedEmployee(response.data);
      setStep('action');
      await checkEmployeeStatus(employeeId);
      await fetchExistingExceptions(employeeId);
      await fetchWeekSchedule(employeeId);
    } catch (err) {
      console.error('Auto-login failed:', err);
      localStorage.removeItem('employeeId');
    } finally {
      setLoading(false);
    }
  };

  const fetchTodayShifts = async () => {
    try {
      const dashRes = await getLiveDashboard(tenantId);
      setTodayShifts(dashRes.data.filter(d => d.shift_id));
    } catch (err) {
      console.error('Failed to load shifts:', err);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');

    if (!employeeIdInput || !pinInput) {
      setLoginError('Please enter both Employee ID and PIN');
      return;
    }

    if (pinInput.length !== 4) {
      setLoginError('PIN must be 4 digits');
      return;
    }

    try {
      // Verify PIN
      const verifyResponse = await verifyEmployeePin(employeeIdInput, pinInput);
      
      if (verifyResponse.data.success) {
        // Get full employee details
        const employeeResponse = await getEmployee(employeeIdInput);
        setSelectedEmployee(employeeResponse.data);
        
        // Save login if remember me is checked
        if (rememberMe) {
          localStorage.setItem('employeeId', employeeIdInput);
        }
        
        // Proceed to action screen
        setStep('action');
        await checkEmployeeStatus(employeeIdInput);
        await fetchExistingExceptions(employeeIdInput);
        await fetchWeekSchedule(employeeIdInput);
        
        // Clear form
        setEmployeeIdInput('');
        setPinInput('');
      }
    } catch (err) {
      if (err.response && err.response.status === 401) {
        setLoginError('Invalid Employee ID or PIN');
      } else if (err.response && err.response.status === 404) {
        setLoginError('Employee not found');
      } else {
        setLoginError('Login failed. Please try again.');
      }
      console.error('Login error:', err);
    }
  };

  const handleLogout = () => {
    setSelectedEmployee(null);
    setStep('login');
    setIsClockedIn(false);
    setClockedInLocation(null);
    setClockedInShiftId(null);
    setCurrentAssignment(null);
    setExistingExceptions([]);
    setWeekSchedule([]);
    setTodayShifts([]);
    localStorage.removeItem('employeeId');
    setMessage(null);
  };

  const checkEmployeeStatus = async (employeeId) => {
    try {
      const response = await getTodayAssignments(employeeId);
      const assignments = response.data;
      
      const activeAssignment = assignments.find(a => a.clock_in && !a.clock_out && a.status === 'working');
      
      if (activeAssignment) {
        setCurrentAssignment(activeAssignment);
        setIsClockedIn(true);
        setClockedInShiftId(activeAssignment.shift_id);
        // Fetch shifts to get location name
        const dashRes = await getLiveDashboard(tenantId);
        const shifts = dashRes.data.filter(d => d.shift_id);
        const shift = shifts.find(s => s.shift_id === activeAssignment.shift_id);
        setClockedInLocation(shift ? shift.location_name : 'Unknown');
      } else {
        setCurrentAssignment(null);
        setIsClockedIn(false);
        setClockedInLocation(null);
        setClockedInShiftId(null);
      }
    } catch (err) {
      console.error('Failed to check employee status:', err);
    }
  };

  const fetchExistingExceptions = async (employeeId) => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const futureDate = new Date();
      futureDate.setMonth(futureDate.getMonth() + 3);
      const futureDateStr = futureDate.toISOString().split('T')[0];
      
      const response = await getEmployeeAvailability(employeeId, today, futureDateStr);
      const exceptions = response.data;
      setExistingExceptions(exceptions);
    } catch (err) {
      console.error('Failed to fetch availability exceptions:', err);
      setExistingExceptions([]);
    }
  };

  const fetchWeekSchedule = async (employeeId) => {
  try {
    const today = new Date();
    const day = today.getDay();
    const diff = today.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(today);
    monday.setDate(diff);
    // Use local date format to avoid timezone issues
    const mondayStr = `${monday.getFullYear()}-${String(monday.getMonth() + 1).padStart(2, '0')}-${String(monday.getDate()).padStart(2, '0')}`;
    
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    const sundayStr = `${sunday.getFullYear()}-${String(sunday.getMonth() + 1).padStart(2, '0')}-${String(sunday.getDate()).padStart(2, '0')}`;
      
      const response = await api.get(`/assignments/employee/${employeeId}/week`, { params: { start_date: mondayStr, end_date: sundayStr } });
      const schedule = response.data;
      setWeekSchedule(schedule);
    } catch (err) {
      console.error('Failed to fetch week schedule:', err);
      setWeekSchedule([]);
    }
  };

  const handleClockIn = async (shift) => {
    if (isClockedIn) {
      setMessage({ type: 'error', text: 'You are already clocked in!' });
      return;
    }
    try {
      const response = await createAssignment({
        shift_id: shift.shift_id,
        employee_id: selectedEmployee.id
      });
      const assignment = response.data;
      
      await clockIn(assignment.id);
      setCurrentAssignment(assignment);
      setIsClockedIn(true);
      setClockedInLocation(shift.location_name);
      setClockedInShiftId(shift.shift_id);
      setMessage({ type: 'success', text: `Clocked in at ${shift.location_name}!` });
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to clock in. You may already be clocked in.' });
      console.error(err);
    }
  };

  const handleClockOut = async () => {
    if (!currentAssignment) {
      setMessage({ type: 'error', text: 'You are not clocked in.' });
      return;
    }
    try {
      await clockOut(currentAssignment.id);
      setMessage({ type: 'success', text: 'Clocked out successfully!' });
      setCurrentAssignment(null);
      setIsClockedIn(false);
      setClockedInLocation(null);
      setClockedInShiftId(null);
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to clock out.' });
      console.error(err);
    }
  };

  const handleStartReport = (shift) => {
    if (!isClockedIn || clockedInShiftId !== shift.shift_id) {
      setMessage({ type: 'error', text: 'You must be clocked in at this location to submit a report.' });
      return;
    }
    setSelectedShift(shift);
    setStep('report');
    setMessage(null);
  };

  const handleSubmitReport = async (e) => {
    e.preventDefault();
    try {
      await submitReport({
        shift_id: selectedShift.shift_id,
        reported_by: selectedEmployee.id,
        reservations: parseInt(reportData.reservations) || 0,
        total_cars: parseInt(reportData.total_cars) || 0,
        free_cars: parseInt(reportData.free_cars) || 0,
        paid_cars: parseInt(reportData.paid_cars) || 0,
        cars_on_lot: parseInt(reportData.cars_on_lot) || 0,
        cash_collected: parseFloat(reportData.cash_collected) || 0,
        card_collected: parseFloat(reportData.card_collected) || 0,
        venmo_collected: 0,
        is_final: reportData.is_final
      });
      setMessage({ type: 'success', text: 'Report submitted successfully!' });
      setReportData({
        reservations: '',
        total_cars: '',
        free_cars: '',
        paid_cars: '',
        cars_on_lot: '',
        cash_collected: '',
        card_collected: '',
        is_final: false
      });
      setTimeout(() => {
        setStep('action');
        setSelectedShift(null);
      }, 2000);
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to submit report.' });
      console.error(err);
    }
  };

  const handleStartAvailability = () => {
    setStep('availability');
    setMessage(null);
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const tomorrowStr = tomorrow.toISOString().split('T')[0];
    setAvailabilityData({
      start_date: tomorrowStr,
      end_date: tomorrowStr,
      reason: 'personal',
      notes: ''
    });
  };

  const handleSubmitAvailability = async (e) => {
    e.preventDefault();
    
    if (!availabilityData.start_date || !availabilityData.end_date) {
      setMessage({ type: 'error', text: 'Please select both start and end dates.' });
      return;
    }

    if (availabilityData.end_date < availabilityData.start_date) {
      setMessage({ type: 'error', text: 'End date cannot be before start date.' });
      return;
    }

    try {
      const startDate = new Date(availabilityData.start_date);
      const endDate = new Date(availabilityData.end_date);
      
      for (let date = new Date(startDate); date <= endDate; date.setDate(date.getDate() + 1)) {
        const dateStr = date.toISOString().split('T')[0];
        
        await submitAvailabilityException({
          employee_id: selectedEmployee.id,
          exception_date: dateStr,
          reason: availabilityData.reason
        });
      }

      setMessage({ type: 'success', text: 'Time off request submitted!' });
      setAvailabilityData({
        start_date: '',
        end_date: '',
        reason: 'personal',
        notes: ''
      });
      
      await fetchExistingExceptions(selectedEmployee.id);
      
      setTimeout(() => {
        setStep('action');
      }, 2000);
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to submit time off request.' });
      console.error(err);
    }
  };

  const handleDeleteException = async (exceptionId) => {
    try {
      await deleteAvailabilityException(exceptionId);

      setMessage({ type: 'success', text: 'Exception removed.' });
      await fetchExistingExceptions(selectedEmployee.id);
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to remove exception.' });
      console.error(err);
    }
  };

  const handleBack = () => {
    if (step === 'report' || step === 'availability') {
      setStep('action');
      setSelectedShift(null);
    }
    setMessage(null);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Invalid Date';
    const cleanDate = dateStr.split('T')[0];
    const date = new Date(cleanDate + 'T00:00:00');
    if (isNaN(date.getTime())) return 'Invalid Date';
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const getReasonLabel = (reason) => {
    const labels = {
      vacation: 'Vacation',
      sick: 'Sick',
      personal: 'Personal',
      other: 'Other'
    };
    return labels[reason] || reason;
  };

  // Mobile-optimized styles
  const styles = {
    container: {
      minHeight: '100vh',
      backgroundColor: '#1e3a5f',
      paddingBottom: 'env(safe-area-inset-bottom)'
    },
    content: {
      maxWidth: '500px',
      margin: '0 auto',
      padding: '16px',
      paddingTop: '24px'
    },
    header: {
      textAlign: 'center',
      marginBottom: '24px',
      paddingTop: '16px'
    },
    logo: {
      fontSize: '28px',
      fontWeight: 'bold',
      color: '#ffffff',
      marginBottom: '4px'
    },
    subtitle: {
      color: '#94a3b8',
      fontSize: '16px'
    },
    message: {
      padding: '16px',
      borderRadius: '12px',
      marginBottom: '20px',
      textAlign: 'center',
      fontSize: '16px',
      fontWeight: '500'
    },
    messageSuccess: {
      backgroundColor: '#dcfce7',
      color: '#166534'
    },
    messageError: {
      backgroundColor: '#fee2e2',
      color: '#dc2626'
    },
    card: {
      backgroundColor: '#ffffff',
      borderRadius: '16px',
      padding: '20px',
      marginBottom: '16px',
      boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
    },
    cardTitle: {
      fontSize: '18px',
      fontWeight: '600',
      color: '#1e3a5f',
      marginBottom: '12px'
    },
    cardSubtitle: {
      fontSize: '14px',
      color: '#6b7280',
      marginBottom: '12px'
    },
    actionButton: {
      width: '100%',
      padding: '18px',
      borderRadius: '12px',
      border: 'none',
      fontSize: '18px',
      fontWeight: '600',
      cursor: 'pointer',
      marginBottom: '10px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '10px'
    },
    clockInButton: {
      backgroundColor: '#22c55e',
      color: '#ffffff'
    },
    clockOutButton: {
      backgroundColor: '#ef4444',
      color: '#ffffff'
    },
    reportButton: {
      backgroundColor: '#3b82f6',
      color: '#ffffff'
    },
    availabilityButton: {
      backgroundColor: '#f59e0b',
      color: '#ffffff'
    },
    logoutButton: {
      backgroundColor: '#6b7280',
      color: '#ffffff'
    },
    disabledButton: {
      backgroundColor: '#d1d5db',
      color: '#9ca3af',
      cursor: 'not-allowed'
    },
    backButton: {
      background: 'none',
      border: 'none',
      color: '#ffffff',
      fontSize: '16px',
      padding: '8px 0',
      marginBottom: '16px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    statusBadge: {
      padding: '16px',
      borderRadius: '12px',
      textAlign: 'center',
      marginBottom: '20px',
      fontSize: '16px',
      fontWeight: '500'
    },
    clockedInBadge: {
      backgroundColor: '#dcfce7',
      color: '#166534'
    },
    greeting: {
      textAlign: 'center',
      marginBottom: '24px'
    },
    greetingName: {
      fontSize: '24px',
      fontWeight: 'bold',
      color: '#ffffff',
      marginBottom: '4px'
    },
    greetingText: {
      color: '#94a3b8',
      fontSize: '16px'
    },
    input: {
      width: '100%',
      padding: '14px',
      fontSize: '16px',
      borderRadius: '10px',
      border: '2px solid #e5e7eb',
      marginBottom: '16px',
      boxSizing: 'border-box'
    },
    label: {
      display: 'block',
      fontSize: '14px',
      fontWeight: '600',
      color: '#374151',
      marginBottom: '6px'
    },
    select: {
      width: '100%',
      padding: '14px',
      fontSize: '16px',
      borderRadius: '10px',
      border: '2px solid #e5e7eb',
      marginBottom: '16px',
      backgroundColor: '#ffffff',
      boxSizing: 'border-box'
    },
    textarea: {
      width: '100%',
      padding: '14px',
      fontSize: '16px',
      borderRadius: '10px',
      border: '2px solid #e5e7eb',
      marginBottom: '16px',
      resize: 'vertical',
      minHeight: '80px',
      boxSizing: 'border-box'
    },
    checkbox: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      marginBottom: '16px'
    },
    checkboxInput: {
      width: '20px',
      height: '20px'
    },
    exceptionItem: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '12px',
      backgroundColor: '#fef3c7',
      borderRadius: '8px',
      marginBottom: '8px'
    },
    deleteButton: {
      background: 'none',
      border: 'none',
      color: '#ef4444',
      fontSize: '20px',
      cursor: 'pointer',
      padding: '4px 8px'
    }
  };

  if (loading) {
    return (
      <div style={{...styles.container, display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
        <p style={{color: '#ffffff', fontSize: '20px'}}>Loading...</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.content}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.logo}>🚗 In House Valet</div>
          <div style={styles.subtitle}>Employee Portal</div>
        </div>

        {/* Message */}
        {message && (
          <div style={{
            ...styles.message,
            ...(message.type === 'success' ? styles.messageSuccess : styles.messageError)
          }}>
            {message.text}
          </div>
        )}

        {/* Step: Login */}
        {step === 'login' && (
          <div style={styles.card}>
            <div style={styles.cardTitle}>🔐 Employee Login</div>
            
            <form onSubmit={handleLogin}>
              <label style={styles.label}>Employee ID</label>
              <input
                type="number"
                inputMode="numeric"
                value={employeeIdInput}
                onChange={(e) => setEmployeeIdInput(e.target.value)}
                style={styles.input}
                placeholder="Enter your employee ID"
                autoFocus
                required
              />
              
              <label style={styles.label}>PIN</label>
              <input
                type="password"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength="4"
                value={pinInput}
                onChange={(e) => setPinInput(e.target.value.replace(/\D/g, ''))}
                style={{
                  ...styles.input,
                  fontSize: '24px',
                  letterSpacing: '8px',
                  textAlign: 'center'
                }}
                placeholder="••••"
                required
              />
              
              {loginError && (
                <div style={{
                  padding: '12px',
                  backgroundColor: '#fee',
                  color: '#c00',
                  borderRadius: '8px',
                  marginBottom: '16px',
                  fontSize: '14px'
                }}>
                  {loginError}
                </div>
              )}
              
              <div style={styles.checkbox}>
                <input
                  type="checkbox"
                  id="rememberMe"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  style={styles.checkboxInput}
                />
                <label htmlFor="rememberMe" style={{fontSize: '14px', color: '#374151'}}>
                  Remember me on this device
                </label>
              </div>
              
              <button 
                type="submit" 
                style={{
                  ...styles.actionButton,
                  backgroundColor: '#10b981'
                }}
              >
                Login
              </button>
            </form>
            
            <div style={{
              marginTop: '24px',
              padding: '16px',
              backgroundColor: '#f3f4f6',
              borderRadius: '8px',
              fontSize: '13px',
              color: '#6b7280',
              textAlign: 'center'
            }}>
              💡 Don't know your Employee ID or PIN? Contact your manager.
            </div>
          </div>
        )}

        {/* Step: Action Menu */}
        {step === 'action' && selectedEmployee && (
          <div>
            <div style={styles.greeting}>
              <div style={styles.greetingName}>Hi, {selectedEmployee.first_name}!</div>
              <div style={styles.greetingText}>What would you like to do?</div>
            </div>

            {/* Clocked in status */}
            {isClockedIn && (
              <div style={{...styles.statusBadge, ...styles.clockedInBadge}}>
                ✓ Clocked in at <strong>{clockedInLocation}</strong>
              </div>
            )}

            {/* Clock In */}
            <div style={styles.card}>
              <div style={styles.cardTitle}>⏱️ Clock In</div>
              {todayShifts.length > 0 ? (
                todayShifts.map((shift) => (
                  <button
                    key={shift.shift_id}
                    onClick={() => handleClockIn(shift)}
                    disabled={isClockedIn}
                    style={{
                      ...styles.actionButton,
                      ...(isClockedIn ? styles.disabledButton : styles.clockInButton)
                    }}
                  >
                    {shift.location_name}
                  </button>
                ))
              ) : (
                <p style={styles.cardSubtitle}>No shifts scheduled for today.</p>
              )}
            </div>

            {/* Clock Out */}
            <button
              onClick={handleClockOut}
              disabled={!isClockedIn}
              style={{
                ...styles.actionButton,
                ...(isClockedIn ? styles.clockOutButton : styles.disabledButton)
              }}
            >
              🚪 Clock Out
            </button>

            {/* Submit Report */}
            <div style={styles.card}>
              <div style={styles.cardTitle}>📋 Submit Shift Report</div>
              {isClockedIn ? (
                <button
                  onClick={() => handleStartReport(todayShifts.find(s => s.shift_id === clockedInShiftId))}
                  style={{...styles.actionButton, ...styles.reportButton}}
                >
                  {clockedInLocation}
                </button>
              ) : (
                <p style={styles.cardSubtitle}>You must be clocked in to submit a report.</p>
              )}
            </div>

            {/* Weekly Schedule */}
            <div style={styles.card}>
              <div style={styles.cardTitle}>📋 My Schedule This Week</div>
              {weekSchedule.length === 0 ? (
                <p style={styles.cardSubtitle}>No shifts scheduled this week.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '8px' }}>
                  {weekSchedule.map((shift, index) => {
  // Parse as local date to avoid timezone shift
  const dateParts = shift.shift_date.split('T')[0].split('-').map(Number);
  const shiftDate = new Date(dateParts[0], dateParts[1] - 1, dateParts[2]);
  const isToday = new Date().toDateString() === shiftDate.toDateString();
  const dayName = shiftDate.toLocaleDateString('en-US', { weekday: 'short' });
  const dateStr = shiftDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                    
                    return (
                      <div
                        key={index}
                        style={{
                          padding: '12px',
                          backgroundColor: isToday ? '#fef3c7' : '#f3f4f6',
                          borderRadius: '8px',
                          border: isToday ? '2px solid #f59e0b' : 'none'
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div>
                            <div style={{ fontWeight: '600', color: '#1f2937' }}>
                              {shift.location_name}
                            </div>
                            <div style={{ fontSize: '13px', color: '#6b7280' }}>
                              {dayName}, {dateStr}
                              {shift.scheduled_start && (
                                <span> • {shift.scheduled_start.slice(0, 5)}</span>
                              )}
                            </div>
                          </div>
                          {isToday && (
                            <span style={{
                              backgroundColor: '#f59e0b',
                              color: '#ffffff',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              fontSize: '11px',
                              fontWeight: '600'
                            }}>
                              TODAY
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Availability */}
            <div style={styles.card}>
              <div style={styles.cardTitle}>📅 Time Off</div>
              <button
                onClick={handleStartAvailability}
                style={{...styles.actionButton, ...styles.availabilityButton}}
              >
                Request Time Off
              </button>
              
              {existingExceptions.length > 0 && (
                <div style={{marginTop: '16px'}}>
                  <p style={styles.cardSubtitle}>Your upcoming time off:</p>
                  {existingExceptions.map((exception) => (
                    <div key={exception.id} style={styles.exceptionItem}>
                      <div>
                        <div style={{fontWeight: '500', fontSize: '14px'}}>
                          {formatDate(exception.exception_date)}
                        </div>
                        <div style={{fontSize: '12px', color: '#6b7280'}}>
                          {getReasonLabel(exception.reason)}
                        </div>
                      </div>
                      <button onClick={() => handleDeleteException(exception.id)} style={styles.deleteButton}>
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Logout Button */}
            <button
              onClick={handleLogout}
              style={{...styles.actionButton, ...styles.logoutButton}}
            >
              🚪 Logout
            </button>
          </div>
        )}

        {/* Step: Submit Report */}
        {step === 'report' && selectedShift && (
          <div>
            <button onClick={handleBack} style={styles.backButton}>
              ← Back
            </button>
            
            <div style={styles.card}>
              <div style={styles.cardTitle}>📋 Shift Report</div>
              <div style={styles.cardSubtitle}>{selectedShift.location_name}</div>
              
              <form onSubmit={handleSubmitReport}>
                <label style={styles.label}>Reservations</label>
                <input
                  type="number"
                  value={reportData.reservations}
                  onChange={(e) => setReportData({...reportData, reservations: e.target.value})}
                  style={styles.input}
                  inputMode="numeric"
                />
                
                <label style={styles.label}>Total Cars</label>
                <input
                  type="number"
                  value={reportData.total_cars}
                  onChange={(e) => setReportData({...reportData, total_cars: e.target.value})}
                  style={styles.input}
                  inputMode="numeric"
                />
                
                <label style={styles.label}>Free Cars</label>
                <input
                  type="number"
                  value={reportData.free_cars}
                  onChange={(e) => setReportData({...reportData, free_cars: e.target.value})}
                  style={styles.input}
                  inputMode="numeric"
                />
                
                <label style={styles.label}>Paid Cars</label>
                <input
                  type="number"
                  value={reportData.paid_cars}
                  onChange={(e) => setReportData({...reportData, paid_cars: e.target.value})}
                  style={styles.input}
                  inputMode="numeric"
                />
                
                <label style={styles.label}>Cars on Lot</label>
                <input
                  type="number"
                  value={reportData.cars_on_lot}
                  onChange={(e) => setReportData({...reportData, cars_on_lot: e.target.value})}
                  style={styles.input}
                  inputMode="numeric"
                />
                
                <label style={styles.label}>Cash Collected ($)</label>
                <input
                  type="number"
                  step="0.01"
                  value={reportData.cash_collected}
                  onChange={(e) => setReportData({...reportData, cash_collected: e.target.value})}
                  style={styles.input}
                  inputMode="decimal"
                />
                
                <label style={styles.label}>Card Collected ($)</label>
                <input
                  type="number"
                  step="0.01"
                  value={reportData.card_collected}
                  onChange={(e) => setReportData({...reportData, card_collected: e.target.value})}
                  style={styles.input}
                  inputMode="decimal"
                />
                
                <div style={styles.checkbox}>
                  <input
                    type="checkbox"
                    id="is_final"
                    checked={reportData.is_final}
                    onChange={(e) => setReportData({...reportData, is_final: e.target.checked})}
                    style={styles.checkboxInput}
                  />
                  <label htmlFor="is_final" style={{fontSize: '16px', color: '#374151'}}>
                    Final end-of-shift report
                  </label>
                </div>
                
                <button type="submit" style={{...styles.actionButton, ...styles.reportButton}}>
                  Submit Report
                </button>
              </form>
            </div>
          </div>
        )}

        {/* Step: Submit Availability */}
        {step === 'availability' && (
          <div>
            <button onClick={handleBack} style={styles.backButton}>
              ← Back
            </button>
            
            <div style={styles.card}>
              <div style={styles.cardTitle}>📅 Request Time Off</div>
              <div style={styles.cardSubtitle}>{selectedEmployee.first_name} {selectedEmployee.last_name}</div>
              
              <form onSubmit={handleSubmitAvailability}>
                <label style={styles.label}>Start Date</label>
                <input
                  type="date"
                  value={availabilityData.start_date}
                  onChange={(e) => setAvailabilityData({...availabilityData, start_date: e.target.value})}
                  style={styles.input}
                  min={new Date().toISOString().split('T')[0]}
                  required
                />
                
                <label style={styles.label}>End Date</label>
                <input
                  type="date"
                  value={availabilityData.end_date}
                  onChange={(e) => setAvailabilityData({...availabilityData, end_date: e.target.value})}
                  style={styles.input}
                  min={availabilityData.start_date || new Date().toISOString().split('T')[0]}
                  required
                />
                
                <label style={styles.label}>Reason</label>
                <select
                  value={availabilityData.reason}
                  onChange={(e) => setAvailabilityData({...availabilityData, reason: e.target.value})}
                  style={styles.select}
                >
                  <option value="personal">Personal</option>
                  <option value="vacation">Vacation</option>
                  <option value="sick">Sick</option>
                  <option value="other">Other</option>
                </select>
                
                <label style={styles.label}>Notes (optional)</label>
                <textarea
                  value={availabilityData.notes}
                  onChange={(e) => setAvailabilityData({...availabilityData, notes: e.target.value})}
                  style={styles.textarea}
                  placeholder="Any additional details..."
                />
                
                <button type="submit" style={{...styles.actionButton, ...styles.availabilityButton}}>
                  Submit Request
                </button>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default EmployeePortal;
